import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormstackEntityApplication from '../components/FormstackEntityApplication/FormstackEntityApplication';
import FormstackSkeleton from '../components/FormstackEntityApplication/FormstackEntityApplicationSkeleton';
import Navigation from '../components/Navigation';
import * as RouteNavigator from './RouteNavigator';
import { createFormstackPrefill } from '../actions/formstackPrefillActions';
import { formstackSubmission } from '../actions/applicationActions';
import { fetchContact } from '../actions/contactActions';
import { fetchAddress } from '../actions/addressActions';
import { fetchAccountType } from '../actions/accountTypeActions';
import { fetchRegistration } from '../actions/registrationActions';
import { fetchSalesReps } from '../actions/salesRepsActions';
import { fetchCountries } from '../actions/countriesActions';
import { trackEvent } from '../lib/amplitude';
import { trackFormstackEmbed } from '../lib/configureDatadog';
import Config from '../Config';

const config = new Config();

// Both rejections classified here carry a status and message only (toSafeError for the prefill,
// the same shape from formstackSubmission); the Response, whose body is the PII prefill payload
// and whose headers carry the auth token, is already stripped.
// A hung request has no HTTP status, so it must be classified before the status check or it
// reports as an ordinary 'network' failure and the two become inseparable in RUM.
const errorKindFor = (error) => {
  if (error && error.isTimeout) return 'timeout';
  return error && error.status ? `http_${error.status}` : 'network';
};

export class FormstackEntityApplicationContainer extends React.Component {
  constructor(props) {
    super(props);
    this.handleFormLoad = this.handleFormLoad.bind(this);
    this.handleFormError = this.handleFormError.bind(this);
    this.handleFormSubmitted = this.handleFormSubmitted.bind(this);
    this.handleBack = this.handleBack.bind(this);
  }

  componentDidMount() {
    // Time-to-interactive (FR-015) is measured from here, not from the embed's own load callback:
    // the applicant starts waiting when the route mounts, and the prefill round-trip happens
    // before the iframe is even rendered.
    this.mountedAt = Date.now();

    // FR-002 replay guard. Every call to the proxy mints a vendor form session, so re-requesting
    // one the store already holds — on a remount from Back, say — orphans the first.
    if (this.hasPrefillSession()) return;

    // Load the application data the prefill depends on before building the request. On a direct
    // load or reload of this route only applicationId and authToken survive (from sessionStorage),
    // so the other slices must be fetched or the prefill body would carry just the application id.
    // A dependency that fails must not strand the applicant on the skeleton: request the prefill
    // regardless and let the proxy fill in what it can.
    const prefillStartedAt = Date.now();
    this.loadPrefillData()
      .catch(() => {})
      .then(() => this.props.createFormstackPrefill())
      .then(() => this.trackPhase('prefill', 'success', prefillStartedAt))
      .catch(error => this.trackPhase('prefill', 'failure', prefillStartedAt, error));
  }

  // The proxy also returns a `url`, but it is discarded: only the id is appended to the configured
  // form base. Guarding on the id is what keeps a url-without-id response from producing '?null'.
  // The id is encoded because it is an API-returned value being interpolated into a URL that
  // becomes a script source; encodeURIComponent leaves the alphanumeric ids Formstack issues
  // untouched, so this is defence in depth rather than a behaviour change (T042).
  getFormUrl() {
    const { prefillId } = this.props;

    if (!prefillId) return config.formstackEntityUrl;
    return `${config.formstackEntityUrl}?${encodeURIComponent(prefillId)}`;
  }

  trackPhase(phase, outcome, startedAt, error) {
    trackFormstackEmbed({
      phase,
      outcome,
      durationMs: Date.now() - startedAt,
      applicationId: this.props.applicationId,
      errorKind: error ? errorKindFor(error) : undefined,
    });
  }

  hasPrefillSession() {
    const { prefillId, prefillStatus } = this.props;
    return Boolean(prefillId) || prefillStatus === 'loading' || prefillStatus === 'success';
  }

  loadPrefillData() {
    const {
      applicationId,
      authToken,
      contact,
      address,
      accountType,
      registration,
      salesReps,
      countries,
    } = this.props;

    const pending = [];

    if (applicationId) {
      if (!contact) pending.push(this.props.fetchContact(applicationId, authToken));
      if (!address) pending.push(this.props.fetchAddress(applicationId, authToken));
      if (!accountType) pending.push(this.props.fetchAccountType(applicationId, authToken));
      if (!registration) pending.push(this.props.fetchRegistration(applicationId, authToken));
    }

    if (!salesReps || salesReps.length === 0) {
      pending.push(this.props.fetchSalesReps());
    }

    // The two country fields transmit the country's full name, because the live form renders them
    // as dropdowns keyed on the name rather than the ISO code. Resolving the name needs this
    // slice, and nothing earlier in a resumed session loads it: arriving forward from
    // /account-type it is already populated, but on a direct load of this route it is empty and
    // both fields would resolve to undefined and be dropped silently.
    if (!countries || countries.length === 0) {
      pending.push(this.props.fetchCountries());
    }

    return Promise.all(pending);
  }

  // The resolved form URL never leaves this component. It carries the prefill id, which is
  // bearer-style: anyone holding it can open a form rendering the applicant's name, address,
  // email and phone. Report whether a prefill was applied, never which one.
  handleFormLoad(loadTimeMs) {
    trackEvent('aop_formstack_embed_loaded', {
      loadTimeMs,
      hasPrefill: Boolean(this.props.prefillId),
    });

    // Two legs: `load` attributes the embed itself, `interactive` is the whole wait the applicant
    // experienced and the only one the 3s budget is measured against.
    trackFormstackEmbed({
      phase: 'load',
      outcome: 'success',
      durationMs: loadTimeMs,
      applicationId: this.props.applicationId,
    });
    trackFormstackEmbed({
      phase: 'interactive',
      outcome: 'success',
      durationMs: Date.now() - this.mountedAt,
      applicationId: this.props.applicationId,
    });
  }

  handleFormError(errorType, errorMessage) {
    trackFormstackEmbed({
      phase: 'load',
      outcome: 'failure',
      durationMs: Date.now() - this.mountedAt,
      applicationId: this.props.applicationId,
      errorKind: errorType,
    });
    trackEvent('aop_formstack_embed_error', {
      errorType,
      errorMessage,
      hasPrefill: Boolean(this.props.prefillId),
    });
  }

  // Moves the application to `submitted` in ellis, which is what takes it off the applicant's
  // pending-applications list. Never rejects: the embed goes on to the confirmation page once
  // this settles either way, since Formstack already holds the form, so a failure is reported
  // to RUM rather than to the applicant.
  handleFormSubmitted() {
    const { applicationId, authToken } = this.props;
    const startedAt = Date.now();
    return this.props.formstackSubmission(applicationId, authToken)
      .then(() => this.trackPhase('submission', 'success', startedAt))
      .catch(error => this.trackPhase('submission', 'failure', startedAt, error));
  }

  // FR-001. Mirrors the PDF path's destination (EntityApplicationContainer.handleBack) rather
  // than introducing a second idiom for the same step. Returning here and forward again remounts
  // this container, which the FR-002 replay guard above is what makes safe.
  // eslint-disable-next-line class-methods-use-this
  handleBack() {
    RouteNavigator.push('/account-type');
  }

  render() {
    const { applicationId, prefillStatus } = this.props;

    // Hold the embed behind the skeleton until the prefill settles so it mounts once with the
    // resolved URL. The embed deliberately ignores later formUrl changes (its shouldComponentUpdate).
    // A failed prefill still renders: a blank form is completable, a dead end is not (FR-008).
    const isSettling = prefillStatus === 'idle' || prefillStatus === 'loading';

    return (
      <div>
        {isSettling
          ? <FormstackSkeleton />
          : (
            <FormstackEntityApplication
              formUrl={this.getFormUrl()}
              idParam={applicationId}
              onFormLoad={this.handleFormLoad}
              onFormError={this.handleFormError}
              onFormSubmitted={this.handleFormSubmitted}
            />
          )}
        {/*
          Outside the conditional on purpose: the prefill round-trip happens before the iframe
          exists, so a Back that appeared only with the embed would strand the applicant for the
          whole wait — and permanently if the embed never rendered.
          Back only, per the mockup. Formstack owns submission and its own Save & Resume, so an
          AOP "next" would submit nothing and an AOP "Save for later" would claim to save a form
          this app cannot read.
        */}
        <div className="row">
          <div className="col col-xs-12">
            <Navigation
              onBack={this.handleBack}
              showBack
              showNext={false}
              showSaveForLater={false}
              backButtonId="back_btn_application"
              isEntityPage
            />
          </div>
        </div>
      </div>
    );
  }
}

FormstackEntityApplicationContainer.propTypes = {
  applicationId: PropTypes.string,
  authToken: PropTypes.string,
  prefillId: PropTypes.string,
  prefillStatus: PropTypes.string,
  contact: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  address: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  accountType: PropTypes.shape(),
  registration: PropTypes.shape(),
  salesReps: PropTypes.arrayOf(PropTypes.shape()),
  countries: PropTypes.arrayOf(PropTypes.shape()),
  createFormstackPrefill: PropTypes.func.isRequired,
  formstackSubmission: PropTypes.func.isRequired,
  fetchContact: PropTypes.func.isRequired,
  fetchAddress: PropTypes.func.isRequired,
  fetchAccountType: PropTypes.func.isRequired,
  fetchRegistration: PropTypes.func.isRequired,
  fetchSalesReps: PropTypes.func.isRequired,
  fetchCountries: PropTypes.func.isRequired,
};

FormstackEntityApplicationContainer.defaultProps = {
  applicationId: '',
  authToken: '',
  prefillId: null,
  prefillStatus: 'idle',
  contact: null,
  address: null,
  accountType: null,
  registration: null,
  salesReps: [],
  countries: [],
};

const mapStateToProps = state => ({
  applicationId: state.applicationId || '',
  authToken: state.authToken || '',
  prefillId: state.formstackPrefill ? state.formstackPrefill.id : null,
  prefillStatus: state.formstackPrefill ? state.formstackPrefill.status : 'idle',
  contact: state.contact,
  address: state.address,
  accountType: state.accountType,
  registration: state.registration,
  salesReps: state.salesReps,
  countries: state.countries,
});

const mapDispatchToProps = {
  createFormstackPrefill,
  formstackSubmission,
  fetchContact,
  fetchAddress,
  fetchAccountType,
  fetchRegistration,
  fetchSalesReps,
  fetchCountries,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormstackEntityApplicationContainer);
